import { COMMA, SEMICOLON } from '@angular/cdk/keycodes';
import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatChipGrid } from '@angular/material/chips';
import { SubscriptSizing } from '@angular/material/form-field';
import { ClientEnvironment, ENVIRONMENT } from '@thehubz/client/core';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { waitForElement } from '@thehubz/shared/utils';
import { MatChipInputEvent } from '../../mat-chip-patched/chip-input';

@Component({
  selector: 'sp-chips-grid-row',
  templateUrl: './chips-grid-row.component.html',
  styleUrls: ['./chips-grid-row.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => ChipsGridRowComponent),
    },
  ],
})
export class ChipsGridRowComponent implements OnChanges, ControlValueAccessor {
  /** Auto complete source */
  @Input() autoCompSource!: { _id: string; name: string }[];
  /**  Data-source for chip */
  @Input() dataSource!: string[];
  /** Check for required to displace error message */
  @Input() required!: boolean;
  /** Required text */
  @Input() requiredText!: string;
  /** Input placeholder */
  @Input() placeholder!: string;
  /** Input matTooltip */
  @Input() chipInputTooltip!: string;
  /** Set mat-chip disable */
  @Input() isDisabled = false;
  /** Mat form field appearance */
  @Input() subscriptSizing: SubscriptSizing = 'fixed'; // mat form field appearance
  /** Tag to highlight */
  @Input() tagToHighlight!: string;

  @Output() removeChip = new EventEmitter<AnyType>();
  @Output() matChipInputToken = new EventEmitter<AnyType>();

  @ViewChild('chipInput') chipInput!: ElementRef<HTMLInputElement>;
  @ViewChild('chipGrid') chipGrid!: MatChipGrid;

  readonly separatorKeysCodes: number[] = [COMMA];

  select = new FormControl();

  constructor(@Inject(ENVIRONMENT) private env: ClientEnvironment) {
    if ([CustomClients.LufthansaMediacenter, CustomClients.LufthansaMedialounge].includes(env.brandingKey)) {
      this.separatorKeysCodes = [SEMICOLON];
    }
  }

  onChange!: (value: string | null | AnyType) => void;
  onTouched!: () => void;

  writeValue(value?: string[]): void {
    if (value?.length) this.select.patchValue(value);
  }

  registerOnChange(fn: (value: string | null | AnyType) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['required']?.currentValue === true) {
      this.select.setErrors({ required: true });
    }
  }

  onInputChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.onChange(value);
    this.onTouched();
  }

  onBlur() {
    this.onTouched();
  }

  remove(list: AnyType) {
    this.removeChip.emit(list);
  }

  /**
   * Add chip on separatorKeysCodes
   * @param event
   */
  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    // typing and pressing enter should not be possible for LufthansaMediacenter and LufthansaMedialounge
    if ([CustomClients.LufthansaMediacenter, CustomClients.LufthansaMedialounge].includes(this.env.brandingKey)) {
      event.chipInput.clear();
      this.removeAutoComp();
      const tagData = this.select.value?.length ? this.select.value : null;
      this.onChange(tagData);
      return;
    }

    this.handleValue(value);
    event.chipInput.clear();
    this.removeAutoComp();
  }

  /**
   * mat-autocomplete optionSelected event method
   */
  selected(value: AnyType): void {
    if (!this.checkValueExists(value)) {
      if (this.env.feature?.tagGroups === true) {
        // will emit value in { _id: string; name: string } format
        this.matChipInputToken.emit(value);
      } else {
        this.matChipInputToken.emit(value.name);
      }
    }
    this.chipInput.nativeElement.value = '';
    this.removeAutoComp();
  }

  removeAutoComp() {
    this.autoCompSource = [];
  }

  addTabOnEnter(event: AnyType) {
    const value = (event.target as HTMLInputElement).value?.trim() || '';

    // typing and pressing enter should not be possible for LufthansaMediacenter and LufthansaMedialounge
    if ([CustomClients.LufthansaMedialounge].includes(this.env.brandingKey)) {
      (event.target as HTMLInputElement).value = '';
      this.removeAutoComp();
      const tagData = this.select.value?.length ? this.select.value : null;
      this.onChange(tagData);
      return;
    }

    const separator = ',';
    this.handleValue(value, separator);

    (event.target as HTMLInputElement).value = '';
    this.removeAutoComp();
  }

  private checkValueExists(value: string): boolean {
    return this.dataSource.some((item) => item === value);
  }

  private handleValue(value: string, separator?: string) {
    if (!value) return;

    if (separator && value.includes(separator)) {
      this.handleSeparate(value, separator);
    } else if (!this.checkValueExists(value)) {
      const tagValue = this.extractTagObject(value);
      this.matChipInputToken.emit(tagValue);
    }
  }

  // extract a tag object from autoCompSource
  extractTagObject(value: string) {
    return this.autoCompSource.find((tag) => tag.name.toLowerCase() === value.toLowerCase()) || value;
  }

  private handleSeparate(valueString: string, splitString: string) {
    valueString.split(splitString).forEach((singleString: string) => {
      const tag = singleString.trim();
      if (tag && !this.checkValueExists(tag)) {
        this.matChipInputToken.emit(tag);
      }
    });
  }

  updatePanelHeight() {
    const input = this.chipInput.nativeElement;

    if (!input) return;

    const formField = input.closest('.mat-mdc-text-field-wrapper');
    const anchorEl = formField || input;
    const anchorRect = anchorEl.getBoundingClientRect();

    // Calculate available space below and above
    const minHeight = 100;
    const offset = 20;
    const spaceBelow = Math.max(Math.floor(window.innerHeight - anchorRect.bottom - offset), minHeight);
    const spaceAbove = Math.max(Math.floor(anchorRect.top - offset), minHeight);

    waitForElement('.sp-dynamic-height-panel').then((panel: HTMLElement) => {
      if (!panel) return;

      const timeUntilPanelGotPositioned = 50;

      setTimeout(() => {
        // Check if panel is rendering above or below
        const isPanelAbove = panel.closest('[class*="panel-above"]') !== null;

        // Set the max height based on position
        const maxHeight = isPanelAbove ? spaceAbove : spaceBelow;

        panel.style.setProperty('--sp-panel-max-height', `${maxHeight}px`);

        // Show the panel when it is ready
        panel.classList.add('visible');
      }, timeUntilPanelGotPositioned);
    });
  }
}
